import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { api_url_2 } from "../Hooks/Global";
import CropModal from './CropModal'; // Adjust path as needed
import Select from 'react-select';
import countryList from 'react-select-country-list';

const StudentRegistrationForm = () => {
    const navigate = useNavigate();
    const { franchise_id, franchise_name } = useParams();
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [studentDetails, setStudentDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Crop modal states
    const [selectedImage, setSelectedImage] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImageURL, setCroppedImageURL] = useState('');
    const [croppedSignURL, setCroppedSignURL] = useState('');
    const [currentCropField, setCurrentCropField] = useState('');

    // Refs for file inputs
    const photoInputRef = useRef(null);
    const signInputRef = useRef(null);

    const countryOptions = countryList().getData();

    const capitalizeWords = (value) => {
        return value
            .toLowerCase()
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    // API Configuration
    const apiURL = api_url_2;

    const [formData, setFormData] = useState({
        student_full_name: '',
        father_name: '',
        mother_name: '',
        mobile_number: '',
        whatsapp_number: '',
        email_address: '',
        full_address: '',
        photo: null,
        signature: null,
        gender: "Male",
        student_status: "1",
        student_city: "",
        student_pincode: "",
        student_aadhar_no: "",
        login_password: "",
        student_dob: "",
        student_education: "",
        student_country: "India",
    });

    const [errors, setErrors] = useState({});

    // Handle file selection for cropping
    const handleImageChange = (e, field) => {
        const file = e.target.files?.[0];

        if (file) {
            // Validate file size (2MB)
            const maxSize = 2 * 1024 * 1024;
            if (file.size > maxSize) {
                setErrors((prev) => ({
                    ...prev,
                    [field]: "File size should not exceed 2 MB",
                }));
                e.target.value = '';
                return;
            }

            setSelectedImage(file);
            setCurrentCropField(field);
            setShowCropModal(true);

            // Clear the file input value
            e.target.value = '';
        }
    };

    // Handle crop completion
    const handleCropComplete = (croppedFile) => {
        if (currentCropField === 'photo') {
            setFormData(prev => ({
                ...prev,
                photo: croppedFile
            }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setCroppedImageURL(reader.result);
            };
            reader.readAsDataURL(croppedFile);
            setErrors(prev => ({ ...prev, photo: "" }));
        } else if (currentCropField === 'signature') {
            setFormData(prev => ({
                ...prev,
                signature: croppedFile
            }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setCroppedSignURL(reader.result);
            };
            reader.readAsDataURL(croppedFile);
            setErrors(prev => ({ ...prev, signature: "" }));
        }
    };

    // Handle close crop modal
    const handleCloseCropModal = () => {
        setShowCropModal(false);
        setSelectedImage(null);
        setCurrentCropField('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const formattedValue = [
            'student_full_name',
            'father_name',
            'mother_name'
        ].includes(name)
            ? capitalizeWords(value)
            : value;

        setFormData(prev => ({
            ...prev,
            [name]: formattedValue
        }));

        setErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };

    const handleGenderChange = (value) => {
        setFormData(prev => ({ ...prev, gender: value }));
        setErrors(prev => ({ ...prev, gender: "" }));
    };

    const validateForm = () => {
        let newErrors = {};

        // Student Information
        if (!formData?.student_full_name?.trim()) {
            newErrors.student_full_name = "Student Full Name is required";
        }
        if (!formData?.father_name?.trim()) {
            newErrors.father_name = "Father's Name is required";
        }
        if (!formData?.mother_name?.trim()) {
            newErrors.mother_name = "Mother's Name is required";
        }
        if (!formData?.mobile_number?.trim()) {
            newErrors.mobile_number = "Mobile Number is required";
        } else if (!/^\d{10}$/.test(formData.mobile_number.trim())) {
            newErrors.mobile_number = "Mobile Number must be 10 digits";
        }
        if (!formData?.email_address?.trim()) {
            newErrors.email_address = "Email Address is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address.trim())) {
            newErrors.email_address = "Please enter a valid email address";
        }
        if (formData.student_country === "India") {
            if (!formData?.student_aadhar_no?.trim()) {
                newErrors.student_aadhar_no = "Aadhar Card Number is required";
            } else if (!/^\d{12}$/.test(formData.student_aadhar_no.trim())) {
                newErrors.student_aadhar_no = "Aadhar Card Number must be 12 digits";
            }
        }
        if (!formData?.student_city?.trim()) {
            newErrors.student_city = "City is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.student_city.trim())) {
            newErrors.student_city = "City must contain only letters";
        }
        if (!formData?.student_pincode?.trim()) {
            newErrors.student_pincode = "Pincode is required";
        } else if (!/^\d{6}$/.test(formData.student_pincode.trim())) {
            newErrors.student_pincode = "Pincode must be 6 digits";
        }
        if (!formData?.full_address?.trim()) {
            newErrors.full_address = "Full Address is required";
        }
        if (!formData?.login_password?.trim()) {
            newErrors.login_password = "Login Password is required";
        } else if (formData.login_password.trim().length < 6) {
            newErrors.login_password = "Login Password must be at least 6 characters";
        }
        if (!formData?.student_dob?.trim()) {
            newErrors.student_dob = "Date of Birth is required";
        }
        if (!formData?.student_education?.trim()) {
            newErrors.student_education = "Education is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const fetchStudentDetails = async (studentId) => {
        try {
            setLoadingDetails(true);
            const response = await axios.get(`${apiURL}Students/StudentDetails`, {
                params: { student_id: studentId }
            });

            if (response?.data?.success) {
                setStudentDetails(response?.data?.data?.[0]);
                setShowDetails(true);
            } else {
                throw new Error(response?.data?.message || "Failed to fetch student details");
            }
        } catch (error) {
            console.error('Error fetching student details:', error);
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to fetch student details",
                icon: "error"
            });
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isValid = validateForm();
        if (!isValid) {
            Swal.fire({
                title: "Validation Error",
                text: "Please fill in all required fields correctly",
                icon: "error"
            });
            return;
        }

        try {
            setLoading(true);
            const body = new FormData();

            // Append all form data
            body.append("franchise_id", franchise_id || "");
            body.append("student_full_name", formData.student_full_name || "");
            body.append("father_name", formData.father_name || "");
            body.append("mother_name", formData.mother_name || "");
            body.append("mobile_number", formData.mobile_number || "");
            body.append("email_address", formData.email_address || "");
            body.append("student_address", formData.full_address || "");
            body.append("gender", formData.gender || "Male");
            body.append("student_status", formData.student_status || "1");
            body.append("student_city", formData.student_city || "");
            body.append("student_pincode", formData.student_pincode || "");
            body.append("login_password", formData.login_password || "");
            body.append("student_dob", formData.student_dob || "");
            body.append("student_education", formData.student_education || "");
            body.append("student_country", formData.student_country || "");
            body.append(
                "student_aadhar_no",
                formData.student_country === "India"
                    ? formData.student_aadhar_no || ""
                    : ""
            );

            if (formData.photo && formData.photo instanceof File) {
                body.append("photo", formData.photo);
            }
            if (formData.signature && formData.signature instanceof File) {
                body.append("signature", formData.signature);
            }

            const response = await axios.post(`${apiURL}Students/StudentRegistration`, body, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response?.data?.success) {
                const studentId = response?.data?.data?.student_id;

                Swal.fire({
                    title: "Success",
                    html: response.data.message || "Student registered successfully",
                    icon: "success",
                    confirmButtonText: "View Details"
                }).then((result) => {
                    if (result.isConfirmed) {
                        fetchStudentDetails(studentId);
                    }
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: response.data.message || "Failed to register student",
                    icon: "error"
                });
            }
        } catch (error) {
            console.error('Submission error:', error);
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "An unexpected error occurred",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const removePhoto = () => {
        setFormData(prev => ({ ...prev, photo: null }));
        setCroppedImageURL('');
        if (photoInputRef.current) {
            photoInputRef.current.value = '';
        }
    };

    const removeSignature = () => {
        setFormData(prev => ({ ...prev, signature: null }));
        setCroppedSignURL('');
        if (signInputRef.current) {
            signInputRef.current.value = '';
        }
    };

    const handleRegisterAnother = () => {
        setShowDetails(false);
        setStudentDetails(null);
        setFormData({
            student_full_name: '',
            father_name: '',
            mother_name: '',
            mobile_number: '',
            whatsapp_number: '',
            email_address: '',
            full_address: '',
            photo: null,
            signature: null,
            gender: "Male",
            student_status: "1",
            student_city: "",
            student_pincode: "",
            student_aadhar_no: "",
            student_dob: "",
            student_education: "",
            login_password: "",
            student_country: "India",
        });
        setCroppedImageURL('');
        setCroppedSignURL('');
        setErrors({});
    };

    return (
        <div id='wrapper'>
            <div className="inner-page-banner-area" style={{ backgroundImage: 'url("/images/About_images/about_banner.jpg")' }}>
                <div className="container">
                    <div className="pagination-area">
                        <h1>
                            Student Registration
                            {franchise_name && (
                                <span className="d-block fs-4 text-light mt-1">
                                    {decodeURIComponent(franchise_name)}
                                </span>
                            )}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="registration-page-area">
                <div className="container">
                    <h2 className="sidebar-title">{showDetails ? "Student Details" : "Register New Student"}</h2>
                    <div className="registration-details-area inner-page-padding">

                        {!showDetails ? (
                            <form onSubmit={handleSubmit}>
                                {/* Student Information Section */}
                                <div className="row">
                                    <div className="col-lg-12">
                                        <h4 className="mb-3">Student Information</h4>
                                        <hr />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Student Full Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter student full name"
                                                name="student_full_name"
                                                value={formData.student_full_name}
                                                onChange={handleInputChange}
                                            />
                                            {errors.student_full_name && <small className="text-danger">{errors.student_full_name}</small>}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Father's Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter father's name"
                                                name="father_name"
                                                value={formData.father_name}
                                                onChange={handleInputChange}
                                            />
                                            {errors.father_name && <small className="text-danger">{errors.father_name}</small>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Mother's Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter mother's name"
                                                name="mother_name"
                                                value={formData.mother_name}
                                                onChange={handleInputChange}
                                            />
                                            {errors.mother_name && <small className="text-danger">{errors.mother_name}</small>}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Mobile Number <span className="text-danger">*</span></label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                placeholder="Enter mobile number"
                                                name="mobile_number"
                                                value={formData.mobile_number}
                                                onChange={handleInputChange}
                                                maxLength="10"
                                            />
                                            {errors.mobile_number && <small className="text-danger">{errors.mobile_number}</small>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Email Address <span className="text-danger">*</span></label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="Enter email address"
                                                name="email_address"
                                                value={formData.email_address}
                                                onChange={handleInputChange}
                                            />
                                            {errors.email_address && <small className="text-danger">{errors.email_address}</small>}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Login Password <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter login password"
                                                name="login_password"
                                                value={formData.login_password}
                                                onChange={handleInputChange}
                                            />
                                            {errors.login_password && <small className="text-danger">{errors.login_password}</small>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">
                                                Aadhar Card Number
                                                {formData.student_country === "India" && (
                                                    <span className="text-danger"> *</span>
                                                )}
                                            </label>

                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder={
                                                    formData.student_country === "India"
                                                        ? "Enter 12 digit Aadhar number"
                                                        : "Not required for this country"
                                                }
                                                name="student_aadhar_no"
                                                value={formData.student_aadhar_no}
                                                onChange={handleInputChange}
                                                disabled={formData.student_country !== "India"}
                                                maxLength="12"
                                            />

                                            {errors.student_aadhar_no && (
                                                <small className="text-danger">
                                                    {errors.student_aadhar_no}
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">
                                                Country <span className="text-danger">*</span>
                                            </label>

                                            <Select
                                                options={countryOptions}
                                                value={
                                                    countryOptions.find(
                                                        option => option.label === formData.student_country
                                                    ) || null
                                                }
                                                onChange={(selected) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        student_country: selected?.label || "",
                                                        student_aadhar_no:
                                                            selected?.label === "India"
                                                                ? prev.student_aadhar_no
                                                                : ""
                                                    }));

                                                    setErrors(prev => ({
                                                        ...prev,
                                                        student_country: "",
                                                        student_aadhar_no: ""
                                                    }));
                                                }}
                                                placeholder="Select Country"
                                                isClearable
                                            />

                                            {errors.student_country && (
                                                <small className="text-danger">
                                                    {errors.student_country}
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Gender <span className="text-danger">*</span></label>
                                            <div className="d-flex gap-3 mt-2">
                                                <div className="form-check">
                                                    <input
                                                        className=""
                                                        type="radio"
                                                        name="gender"
                                                        value="Male"
                                                        checked={formData.gender === "Male"}
                                                        onChange={() => handleGenderChange("Male")}
                                                    />
                                                    <label className="form-check-label">Male</label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className=""
                                                        type="radio"
                                                        name="gender"
                                                        value="Female"
                                                        checked={formData.gender === "Female"}
                                                        onChange={() => handleGenderChange("Female")}
                                                    />
                                                    <label className="form-check-label">Female</label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className=""
                                                        type="radio"
                                                        name="gender"
                                                        value="Other"
                                                        checked={formData.gender === "Other"}
                                                        onChange={() => handleGenderChange("Other")}
                                                    />
                                                    <label className="form-check-label">Other</label>
                                                </div>
                                            </div>
                                            {errors.gender && <small className="text-danger">{errors.gender}</small>}
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Date of Birth <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                placeholder="Select date of birth"
                                                name="student_dob"
                                                format="yyyy-MM-dd"
                                                value={formData.student_dob}
                                                onChange={handleInputChange}
                                            />
                                            {errors.student_dob && <small className="text-danger">{errors.student_dob}</small>}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Education <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter education"
                                                name="student_education"
                                                value={formData.student_education}
                                                onChange={handleInputChange}
                                            />
                                            {errors.student_education && <small className="text-danger">{errors.student_education}</small>}
                                        </div>
                                    </div>
                                </div>


                                {/* Address Information Section */}
                                <div className="row mt-4">
                                    <div className="col-lg-12">
                                        <h4 className="mb-3">Address Information</h4>
                                        <hr />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">City <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter city"
                                                name="student_city"
                                                value={formData.student_city}
                                                onChange={handleInputChange}
                                            />
                                            {errors.student_city && <small className="text-danger">{errors.student_city}</small>}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Pincode <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter 6 digit pincode"
                                                name="student_pincode"
                                                value={formData.student_pincode}
                                                onChange={handleInputChange}
                                                maxLength="6"
                                            />
                                            {errors.student_pincode && <small className="text-danger">{errors.student_pincode}</small>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Full Address <span className="text-danger">*</span></label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                placeholder="Enter complete address"
                                                name="full_address"
                                                value={formData.full_address}
                                                onChange={handleInputChange}
                                            ></textarea>
                                            {errors.full_address && <small className="text-danger">{errors.full_address}</small>}
                                        </div>
                                    </div>
                                </div>

                                {/* Photo and Signature Section */}
                                <div className="row mt-4">
                                    <div className="col-lg-12">
                                        <h4 className="mb-3">Documents</h4>
                                        <hr />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Student Photo</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="form-control"
                                                ref={photoInputRef}
                                                onChange={(e) => handleImageChange(e, 'photo')}
                                            />
                                            {errors.photo && <small className="text-danger">{errors.photo}</small>}
                                            {(croppedImageURL || (formData.photo && typeof formData.photo === 'string')) && (
                                                <div className="mt-2">
                                                    <img
                                                        src={croppedImageURL || formData.photo}
                                                        alt="Student"
                                                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '10px' }}
                                                    />
                                                    <button type="button" className="btn btn-sm btn-danger ms-2" onClick={removePhoto}>Remove</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label className="control-label">Student Signature</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="form-control"
                                                ref={signInputRef}
                                                onChange={(e) => handleImageChange(e, 'signature')}
                                            />
                                            {errors.signature && <small className="text-danger">{errors.signature}</small>}
                                            {(croppedSignURL || (formData.signature && typeof formData.signature === 'string')) && (
                                                <div className="mt-2">
                                                    <img
                                                        src={croppedSignURL || formData.signature}
                                                        alt="Signature"
                                                        style={{ width: '150px', height: '80px', objectFit: 'contain' }}
                                                    />
                                                    <button type="button" className="btn btn-sm btn-danger ms-2" onClick={removeSignature}>Remove</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div className="pLace-order mt-30">
                                            <button className="view-all-accent-btn" type="submit" disabled={loading}>
                                                {loading ? "Submitting..." : "Submit Registration"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            <div>
                                {loadingDetails ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-3">Loading student details...</p>
                                    </div>
                                ) : (
                                    studentDetails && (
                                        <div className="text-center py-5">
                                            <h2 className="mb-4 text-success">Student Registered Successfully 🎉</h2>

                                            <div className="card p-4 mx-auto" style={{ maxWidth: "500px" }}>
                                                <h4 className="mb-0 fw-bold">
                                                    {studentDetails.student_full_name}
                                                </h4>
                                                <hr />

                                                <p className="d-flex justify-content-between align-items-center">
                                                    <span>
                                                        <strong>Login ID/SNR:</strong> {studentDetails.student_pnr}
                                                    </span>
                                                    <button
                                                        className="btn btn-sm btn-outline-dark"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(studentDetails.student_pnr);
                                                            Swal.fire({
                                                                icon: "success",
                                                                title: "Copied",
                                                                text: "Login ID copied",
                                                                timer: 1200,
                                                                showConfirmButton: false
                                                            });
                                                        }}
                                                    >
                                                        <i className="fa fa-copy"></i>
                                                    </button>
                                                </p>

                                                <p className="d-flex justify-content-between align-items-center">
                                                    <span>
                                                        <strong>Password:</strong> {studentDetails.login_password}
                                                    </span>
                                                    <button
                                                        className="btn btn-sm btn-outline-dark"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(studentDetails.login_password);
                                                            Swal.fire({
                                                                icon: "success",
                                                                title: "Copied",
                                                                text: "Password copied",
                                                                timer: 1200,
                                                                showConfirmButton: false
                                                            });
                                                        }}
                                                    >
                                                        <i className="fa fa-copy"></i>
                                                    </button>
                                                </p>

                                                <div className="alert alert-info mt-3">
                                                    Please use the above credentials to login into the student portal.
                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <button
                                                    className="view-all-accent-btn"
                                                    onClick={handleRegisterAnother}
                                                >
                                                    Register Another Student
                                                </button>
                                                <button
                                                    className="view-all-accent-btn ms-2"
                                                    onClick={() => navigate('/')}
                                                    style={{ backgroundColor: '#6c757d' }}
                                                >
                                                    Go to Home
                                                </button>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Crop Modal */}
            {showCropModal && selectedImage && (
                <CropModal
                    file={selectedImage}
                    onClose={handleCloseCropModal}
                    onCropComplete={handleCropComplete}
                    aspect={currentCropField === 'photo' ? 6 / 7 : 3 / 1}
                />
            )}
        </div>
    );
};

export default StudentRegistrationForm;